import React, { useMemo, useEffect } from "react";
import { Map, useMap, useMapsLibrary, AdvancedMarker } from "@vis.gl/react-google-maps";
import { Box, CircularProgress } from "@mui/material";
import AdjustIcon from "@mui/icons-material/Adjust";
import { Feature, Polygon as GeoJSONPolygon, MultiPolygon, Point, Position, FeatureCollection } from "geojson";
import type { Job } from "../../../../../../models";
import { Polygon } from "../../../../../JobsDashboard/map";

// Colors for different layer types
const BOUNDARY_COLOR = "#2196f3"; // Blue
const ZONES_COLOR = "#4caf50"; // Green
const POINTS_COLOR = "#f44336"; // Red

interface JobCardMapProps {
	job: Job;
	/** Height of the map container. Defaults to 200px. Use "100%" to fill parent. */
	height?: number | string;
}

interface ProcessedMapData {
	polygonElements: React.ReactNode[];
	pointElements: React.ReactNode[];
	allCoordinates: google.maps.LatLngLiteral[];
}

/**
 * Child component to handle bounds fitting (useMap only works inside Map children)
 */
function MapBoundsFitter({ coordinates }: { coordinates: google.maps.LatLngLiteral[] }) {
	const map = useMap();

	useEffect(() => {
		if (!map || coordinates.length === 0) return;

		const bounds = new google.maps.LatLngBounds();
		coordinates.forEach((coord) => bounds.extend(coord));

		// Use idle listener to ensure map is ready
		const idleListener = google.maps.event.addListenerOnce(map, "idle", () => {
			map.fitBounds(bounds, { top: 20, right: 20, bottom: 20, left: 20 });
		});

		// Also try immediately in case map is already idle
		map.fitBounds(bounds, { top: 20, right: 20, bottom: 20, left: 20 });

		return () => {
			google.maps.event.removeListener(idleListener);
		};
	}, [map, coordinates]);

	return null;
}

/**
 * Process a FeatureCollection of polygons into map elements
 */
function processPolygonFeatures(
	featureCollection: FeatureCollection | undefined,
	keyPrefix: string,
	color: string,
	coords: google.maps.LatLngLiteral[],
): React.ReactNode[] {
	if (!featureCollection?.features?.length) return [];

	const polygons: React.ReactNode[] = [];

	featureCollection.features.forEach((feature, featureIndex) => {
		const geometry = feature.geometry;
		if (!geometry || (geometry.type !== "Polygon" && geometry.type !== "MultiPolygon")) return;

		const polygonFeature = feature as Feature<GeoJSONPolygon | MultiPolygon>;
		const coordinates =
			geometry.type === "Polygon" ? [polygonFeature.geometry.coordinates] : polygonFeature.geometry.coordinates;

		// Process each polygon (for MultiPolygon, this iterates over each polygon)
		const paths = coordinates.map((coordinate) => {
			return coordinate
				.map((ring) => {
					return (ring as Position[])
						.map((cc) => {
							const latLng = { lat: cc[1], lng: cc[0] };
							coords.push(latLng);
							return latLng;
						})
						.flat();
				})
				.flat();
		});

		paths.forEach((path, pathIndex) => {
			polygons.push(
				<Polygon
					key={`${keyPrefix}-${featureIndex}-${pathIndex}`}
					paths={path}
					fillColor={color}
					fillOpacity={0.3}
					strokeColor={color}
					strokeWeight={2}
				/>,
			);
		});
	});

	return polygons;
}

/**
 * Process a FeatureCollection of points into map elements
 */
function processPointFeatures(
	featureCollection: FeatureCollection | undefined,
	keyPrefix: string,
	color: string,
	coords: google.maps.LatLngLiteral[],
): React.ReactNode[] {
	if (!featureCollection?.features?.length) return [];

	const points: React.ReactNode[] = [];

	featureCollection.features.forEach((feature, featureIndex) => {
		const geometry = feature.geometry;
		if (!geometry || geometry.type !== "Point") return;

		const pointFeature = feature as Feature<Point>;
		const position = {
			lat: pointFeature.geometry.coordinates[1],
			lng: pointFeature.geometry.coordinates[0],
		};
		coords.push(position);

		points.push(
			<AdvancedMarker key={`${keyPrefix}-${featureIndex}`} position={position}>
				<AdjustIcon sx={{ color, fontSize: 14 }} />
			</AdvancedMarker>,
		);
	});

	return points;
}

/**
 * Map component for JobCard that displays boundaries, points, and zones
 */
export const JobCardMap: React.FC<JobCardMapProps> = ({ job, height = 200 }) => {
	const mapsLibrary = useMapsLibrary("maps");

	// Check if any geodata exists
	const hasGeodata =
		(job.files?.boundaryGeoJSON?.features?.length ?? 0) > 0 ||
		(job.files?.pointsGeoJSON?.features?.length ?? 0) > 0 ||
		(job.files?.zonesGeoJSON?.features?.length ?? 0) > 0;

	// Process all features
	const processedData = useMemo((): ProcessedMapData => {
		const coords: google.maps.LatLngLiteral[] = [];
		const polygons: React.ReactNode[] = [];
		const points: React.ReactNode[] = [];

		// Process boundaries (blue)
		polygons.push(...processPolygonFeatures(job.files?.boundaryGeoJSON, "boundary", BOUNDARY_COLOR, coords));

		// Process zones (green)
		polygons.push(...processPolygonFeatures(job.files?.zonesGeoJSON, "zones", ZONES_COLOR, coords));

		// Process points (red)
		points.push(...processPointFeatures(job.files?.pointsGeoJSON, "points", POINTS_COLOR, coords));

		return {
			polygonElements: polygons,
			pointElements: points,
			allCoordinates: coords,
		};
	}, [job.files?.boundaryGeoJSON, job.files?.zonesGeoJSON, job.files?.pointsGeoJSON]);

	// Don't render if no geodata
	if (!hasGeodata) {
		return null;
	}

	// Loading state while maps library loads
	if (!mapsLibrary) {
		return (
			<Box
				sx={{
					height,
					display: "flex",
					alignItems: "center",
					justifyContent: "center",
					bgcolor: "grey.100",
					borderRadius: height === "100%" ? 0 : 1,
					mt: height === "100%" ? 0 : 2,
				}}
			>
				<CircularProgress size={24} />
			</Box>
		);
	}

	return (
		<Box
			sx={{
				height,
				borderRadius: height === "100%" ? 0 : 1,
				overflow: "hidden",
				mt: height === "100%" ? 0 : 2,
			}}
		>
			<Map
				mapId="customerMaps"
				defaultCenter={{ lat: 40, lng: -95 }}
				defaultZoom={4}
				style={{ width: "100%", height: "100%" }}
				disableDefaultUI={true}
				zoomControl={true}
			>
				<MapBoundsFitter coordinates={processedData.allCoordinates} />
				{processedData.polygonElements}
				{processedData.pointElements}
			</Map>
		</Box>
	);
};
